import React, { useCallback, useEffect, useMemo, useState } from 'react';
import api from '../utils/api';
import { formatUsdAmount } from '../utils/money';
import EventClaimStatsModal from './EventClaimStatsModal';

function pnlClass(n) {
  const v = Number(n) || 0;
  if (v > 0.000001) return 'text-emerald-600 dark:text-emerald-400 font-semibold';
  if (v < -0.000001) return 'text-rose-600 dark:text-rose-400 font-semibold';
  return 'text-gray-700 dark:text-gray-300';
}

function fmt(n) {
  return formatUsdAmount(Number(n) || 0);
}

const PRESETS = [
  { id: '7d', label: '7d' },
  { id: '30d', label: '30d' },
  { id: '90d', label: '90d' },
  { id: 'ytd', label: 'YTD' },
  { id: 'all', label: 'All' },
  { id: 'custom', label: 'Custom' },
];

function rangeForPreset(id) {
  const to = new Date();
  to.setHours(23, 59, 59, 999);
  const from = new Date();
  if (id === '7d') from.setDate(from.getDate() - 7);
  else if (id === '30d') from.setDate(from.getDate() - 30);
  else if (id === '90d') from.setDate(from.getDate() - 90);
  else if (id === 'ytd') {
    from.setMonth(0, 1);
    from.setHours(0, 0, 0, 0);
  } else if (id === 'all') return { from: '', to: '' };
  else return null;
  from.setHours(0, 0, 0, 0);
  return {
    from: from.toISOString().slice(0, 10),
    to: to.toISOString().slice(0, 10),
  };
}

/**
 * Shared Data Room analytics for Admin + SuperAdmin.
 * @param {{ apiBase?: 'admin' | 'superadmin' }} props
 */
export default function DataRoomTab({ apiBase = 'admin' }) {
  const [preset, setPreset] = useState('30d');
  const [from, setFrom] = useState(() => rangeForPreset('30d').from);
  const [to, setTo] = useState(() => rangeForPreset('30d').to);
  const [kind, setKind] = useState('all');
  const [status, setStatus] = useState('all');
  const [resolved, setResolved] = useState('all');
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState(null);
  const [meta, setMeta] = useState(null);
  const [claimItem, setClaimItem] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = new URLSearchParams();
      if (kind && kind !== 'all') params.set('kind', kind);
      if (from) params.set('from', from);
      if (to) params.set('to', to);
      if (status && status !== 'all') params.set('status', status);
      if (resolved !== 'all') params.set('resolved', resolved);
      if (q.trim()) params.set('q', q.trim());
      params.set('limit', '300');
      const { data } = await api.get(`/${apiBase}/data-room?${params.toString()}`);
      setRows(data.rows || []);
      setTotals(data.totals || null);
      setMeta(data.meta || null);
    } catch (e) {
      setError(e.response?.data?.message || e.message || 'Failed to load Data Room');
      setRows([]);
      setTotals(null);
    } finally {
      setLoading(false);
    }
  }, [apiBase, kind, from, to, status, resolved, q]);

  useEffect(() => {
    load();
  }, [load]);

  const onPreset = (id) => {
    setPreset(id);
    const r = rangeForPreset(id);
    if (r) {
      setFrom(r.from);
      setTo(r.to);
    }
  };

  const summaryCards = useMemo(() => {
    if (!totals) return [];
    return [
      { label: 'Events', value: String(totals.eventCount || 0) },
      { label: 'Participants (unique Σ)', value: String(totals.participantsUnique || 0) },
      { label: 'Free / Boost / Market', value: `${totals.participants?.free || 0} / ${totals.participants?.boost || 0} / ${totals.participants?.market || 0}` },
      { label: 'Claimed', value: fmt(totals.claimed), cls: 'text-emerald-600' },
      {
        label: 'Unclaim (F, B, M)',
        value: `${fmt(totals.freeUnclaimed)} / ${fmt(totals.boostUnclaimed)} / ${fmt(totals.marketUnclaimed)}`,
        cls: 'text-amber-600',
      },
      { label: 'Grand Σ', value: fmt(totals.grandTotal) },
      { label: 'Market volume', value: fmt(totals.marketVolume) },
      { label: 'Market P&L', value: fmt(totals.marketPnL), cls: pnlClass(totals.marketPnL) },
      { label: 'Fees total', value: fmt(totals.feesTotal) },
    ];
  }, [totals]);

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h2 className="text-xl font-bold text-gray-900 dark:text-white">Data Room</h2>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 max-w-2xl">
            Advanced analytics per game — participants, claims, volume, and market P&amp;L
            (net buy volume − market payouts). Click an event for the full claim breakdown.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          className="px-3 py-1.5 text-xs font-semibold rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-60"
        >
          {loading ? 'Loading…' : 'Refresh'}
        </button>
      </div>

      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-3 space-y-3">
        <div className="flex flex-wrap gap-1.5">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              onClick={() => onPreset(p.id)}
              className={`px-2.5 py-1 text-[11px] font-semibold rounded-md border ${
                preset === p.id
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
              }`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-2 text-xs">
          <label className="block">
            <span className="text-gray-500 dark:text-gray-400">From</span>
            <input
              type="date"
              value={from}
              onChange={(e) => {
                setPreset('custom');
                setFrom(e.target.value);
              }}
              className="mt-0.5 w-full px-2 py-1.5 rounded border dark:bg-gray-700 dark:text-white dark:border-gray-600"
            />
          </label>
          <label className="block">
            <span className="text-gray-500 dark:text-gray-400">To</span>
            <input
              type="date"
              value={to}
              onChange={(e) => {
                setPreset('custom');
                setTo(e.target.value);
              }}
              className="mt-0.5 w-full px-2 py-1.5 rounded border dark:bg-gray-700 dark:text-white dark:border-gray-600"
            />
          </label>
          <label className="block">
            <span className="text-gray-500 dark:text-gray-400">Kind</span>
            <select
              value={kind}
              onChange={(e) => setKind(e.target.value)}
              className="mt-0.5 w-full px-2 py-1.5 rounded border dark:bg-gray-700 dark:text-white dark:border-gray-600"
            >
              <option value="all">All</option>
              <option value="match">Matches</option>
              <option value="poll">Polls</option>
            </select>
          </label>
          <label className="block">
            <span className="text-gray-500 dark:text-gray-400">Status</span>
            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="mt-0.5 w-full px-2 py-1.5 rounded border dark:bg-gray-700 dark:text-white dark:border-gray-600"
            >
              <option value="all">All</option>
              <option value="upcoming">Upcoming</option>
              <option value="live">Live</option>
              <option value="completed">Completed</option>
              <option value="active">Active</option>
              <option value="settled">Settled</option>
              <option value="locked">Locked</option>
            </select>
          </label>
          <label className="block">
            <span className="text-gray-500 dark:text-gray-400">Resolved</span>
            <select
              value={resolved}
              onChange={(e) => setResolved(e.target.value)}
              className="mt-0.5 w-full px-2 py-1.5 rounded border dark:bg-gray-700 dark:text-white dark:border-gray-600"
            >
              <option value="all">All</option>
              <option value="true">Resolved</option>
              <option value="false">Open</option>
            </select>
          </label>
          <label className="block">
            <span className="text-gray-500 dark:text-gray-400">Search</span>
            <input
              type="search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Team / question"
              className="mt-0.5 w-full px-2 py-1.5 rounded border dark:bg-gray-700 dark:text-white dark:border-gray-600"
            />
          </label>
        </div>
        {meta?.note ? (
          <p className="text-[10px] text-gray-400 dark:text-gray-500">{meta.note}</p>
        ) : null}
      </div>

      {error ? (
        <div className="text-sm text-rose-600 dark:text-rose-400 bg-rose-50 dark:bg-rose-950/40 rounded-lg px-3 py-2">
          {error}
        </div>
      ) : null}

      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2">
        {summaryCards.map((c) => (
          <div
            key={c.label}
            className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-2.5 py-2"
          >
            <div className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400">
              {c.label}
            </div>
            <div className={`text-sm tabular-nums font-bold text-gray-900 dark:text-white ${c.cls || ''}`}>
              {c.value}
            </div>
          </div>
        ))}
      </div>

      <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800">
        <table className="min-w-[1100px] w-full text-[11px] leading-tight">
          <thead className="bg-gray-50 dark:bg-gray-900/60 text-gray-600 dark:text-gray-300 sticky top-0">
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th className="text-left px-2 py-2 font-semibold">Event</th>
              <th className="text-right px-1.5 py-2 font-semibold">Free</th>
              <th className="text-right px-1.5 py-2 font-semibold">Boost</th>
              <th className="text-right px-1.5 py-2 font-semibold">Mkt</th>
              <th className="text-right px-1.5 py-2 font-semibold">Σ part.</th>
              <th className="text-right px-1.5 py-2 font-semibold">Unique</th>
              <th className="text-right px-1.5 py-2 font-semibold text-emerald-700 dark:text-emerald-400">
                Claimed
              </th>
              <th className="text-right px-1.5 py-2 font-semibold text-amber-700 dark:text-amber-400">
                Unclaim (F, B, M)
              </th>
              <th className="text-right px-1.5 py-2 font-semibold">Grand Σ</th>
              <th className="text-right px-1.5 py-2 font-semibold">Volume</th>
              <th className="text-right px-1.5 py-2 font-semibold">Mkt P&amp;L</th>
              <th className="text-right px-1.5 py-2 font-semibold">Fees</th>
              <th className="text-center px-1.5 py-2 font-semibold"> </th>
            </tr>
          </thead>
          <tbody>
            {loading && !rows.length ? (
              <tr>
                <td colSpan={13} className="px-3 py-8 text-center text-gray-500">
                  Loading analytics…
                </td>
              </tr>
            ) : null}
            {!loading && !rows.length ? (
              <tr>
                <td colSpan={13} className="px-3 py-8 text-center text-gray-500">
                  No events match these filters.
                </td>
              </tr>
            ) : null}
            {rows.map((r) => (
              <tr
                key={`${r.kind}-${r.id}`}
                className="border-b border-gray-100 dark:border-gray-700/80 hover:bg-gray-50/80 dark:hover:bg-gray-900/40"
              >
                <td className="px-2 py-1.5 max-w-[220px]">
                  <div className="font-semibold text-gray-900 dark:text-white truncate" title={r.label}>
                    {r.label}
                  </div>
                  <div className="text-[10px] text-gray-400">
                    {r.kind} · {r.status || '—'}
                    {r.isResolved ? ' · resolved' : ''}
                    {r.date ? ` · ${new Date(r.date).toLocaleDateString()}` : ''}
                  </div>
                </td>
                <td className="text-right px-1.5 tabular-nums">{r.participants.free}</td>
                <td className="text-right px-1.5 tabular-nums">{r.participants.boost}</td>
                <td className="text-right px-1.5 tabular-nums">{r.participants.market}</td>
                <td className="text-right px-1.5 tabular-nums font-medium">{r.participants.sumModes}</td>
                <td className="text-right px-1.5 tabular-nums">{r.participants.unique}</td>
                <td className="text-right px-1.5 tabular-nums text-emerald-700 dark:text-emerald-400">
                  {fmt(r.claims.claimed)}
                </td>
                <td className="text-right px-1.5 tabular-nums text-amber-700 dark:text-amber-400 whitespace-nowrap">
                  {fmt(r.claims.unclaimedFree ?? r.claims.free?.unclaimed)} /{' '}
                  {fmt(r.claims.unclaimedBoost ?? r.claims.boost?.unclaimed)} /{' '}
                  {fmt(r.claims.unclaimedMarket ?? r.claims.market?.unclaimed)}
                </td>
                <td className="text-right px-1.5 tabular-nums font-medium">{fmt(r.claims.grandTotal)}</td>
                <td className="text-right px-1.5 tabular-nums">{fmt(r.marketVolume)}</td>
                <td className={`text-right px-1.5 tabular-nums ${pnlClass(r.marketPnL)}`}>
                  {fmt(r.marketPnL)}
                </td>
                <td className="text-right px-1.5 tabular-nums">{fmt(r.fees.total)}</td>
                <td className="text-center px-1.5">
                  <button
                    type="button"
                    onClick={() =>
                      setClaimItem({
                        _id: r.id,
                        kind: r.kind,
                        label: r.label,
                        teamA: r.label,
                      })
                    }
                    className="text-[10px] font-semibold text-blue-600 dark:text-blue-400 hover:underline"
                  >
                    Claims
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
          {totals && rows.length > 0 ? (
            <tfoot className="bg-gray-100 dark:bg-gray-900 font-semibold text-gray-900 dark:text-white">
              <tr>
                <td className="px-2 py-2">Grand total ({totals.eventCount} events)</td>
                <td className="text-right px-1.5 tabular-nums">{totals.participants?.free || 0}</td>
                <td className="text-right px-1.5 tabular-nums">{totals.participants?.boost || 0}</td>
                <td className="text-right px-1.5 tabular-nums">{totals.participants?.market || 0}</td>
                <td className="text-right px-1.5 tabular-nums">{totals.participantsSumModes || 0}</td>
                <td className="text-right px-1.5 tabular-nums">{totals.participantsUnique || 0}</td>
                <td className="text-right px-1.5 tabular-nums text-emerald-700 dark:text-emerald-400">
                  {fmt(totals.claimed)}
                </td>
                <td className="text-right px-1.5 tabular-nums text-amber-700 dark:text-amber-400 whitespace-nowrap">
                  {fmt(totals.freeUnclaimed)} / {fmt(totals.boostUnclaimed)} / {fmt(totals.marketUnclaimed)}
                </td>
                <td className="text-right px-1.5 tabular-nums">{fmt(totals.grandTotal)}</td>
                <td className="text-right px-1.5 tabular-nums">{fmt(totals.marketVolume)}</td>
                <td className={`text-right px-1.5 tabular-nums ${pnlClass(totals.marketPnL)}`}>
                  {fmt(totals.marketPnL)}
                </td>
                <td className="text-right px-1.5 tabular-nums">{fmt(totals.feesTotal)}</td>
                <td />
              </tr>
            </tfoot>
          ) : null}
        </table>
      </div>

      {claimItem ? (
        <EventClaimStatsModal
          kind={claimItem.kind}
          item={claimItem}
          apiBase={apiBase}
          onClose={() => setClaimItem(null)}
        />
      ) : null}
    </div>
  );
}
